import type { LTIPPlan } from "@/types/types";
import { DataGrid } from "devextreme-react";
import { Column, DataGridRef } from "devextreme-react/data-grid";
import { customHeader, cellRenderer } from "../SAR/SARGrid";
import { GridSkeleton } from "@/components/Skeletons";
import { formatNumber } from "devextreme/localization";
import { useRef } from "react";
import { adjustSortArrowPosition, checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useLTIP } from "@/hooks/useLTIP";
import { LTIPType } from "@/types/enums";
import { useParams } from "react-router";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import NoDataMessage from "@/components/NoDataMessage";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import Heading from "@/components/Heading";
import { NoContactBirthDateMessage } from "../HouseholdContact";

export default function DeferredLongTermIncentivePlan() {
  const dxGridRef = useRef<DataGridRef>(null);
  const { id } = useParams();
  const { household, upLoading } = useHeaderChange(id!, "Deferred Long-Term Incentive Plan");
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.DLTIP);

  const {
    data: dltipData,
    isLoading: dltipIsLoading,
    isError: dltipIsError,
    isRefetching: dltipIsRefetching,
  } = useLTIP(id!, LTIPType.DLTIP, selectedClient);

  useDefaultSelectedClient(household, dltipData, selectedClient, setSelectedClient, LTIPType.DLTIP);

  const fa = dltipData?.data[0];
  const ds = fa?.planYears;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {upLoading ? (
        <div className="max-w-[16rem]">
          <GridSkeleton columns={2} rows={1} />
        </div>
      ) : household?.secondaryContact && dltipData?.bothClientsHaveData ? (
        <HouseholdClientTabs
          household={household}
          selectedClientExternalCode={selectedClient}
          setSelectedClientExternalCode={setSelectedClient}
        />
      ) : null}
      {dltipIsLoading || dltipIsRefetching ? <DLTIPSkeleton /> : null}
      {!ds?.length && !dltipIsLoading && !dltipIsRefetching ? <NoDataMessage /> : null}
      {dltipIsError ? <div>Failed fetching Deferred Long-Term Incentive Plan</div> : null}

      {ds?.length && !dltipIsLoading && !dltipIsRefetching ? (
        <>
          {!dltipData?.bothClientsHaveData && !household?.isSingle ? (
            <Heading
              text={`Deferred Long-Term Incentive Plan for ${getOwnerName(household, selectedClient)}`}
            />
          ) : null}
          <DataGrid
            dataSource={ds ?? []}
            showBorders={true}
            showRowLines={true}
            showColumnLines={true}
            keyExpr={"externalCode"}
            className="ao-grid"
            ref={dxGridRef}
            wordWrapEnabled={true}
            onCellPrepared={(e) => {
              if (e.rowType === "header" || e.column.dataField === "planYear") {
                e.cellElement.style.backgroundColor = "var(--primary-color)";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "500";
              }
              adjustSortArrowPosition(dxGridRef);
            }}
          >
            <Column
              dataField="planYear"
              dataType="date"
              allowSorting={false}
              sortOrder={"asc"}
              allowEditing={false}
              width={160}
              alignment={"center"}
              headerCellRender={() => customHeader(`plan-year`, "plan year")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.year ? cellRenderer(cellData, "plan-year", item?.year) : "";
              }}
            />
            <Column
              dataField="deferralType"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`deferral-type`, "deferral type")}
              cellRender={(cellData) => {
                return fa?.deferralType
                  ? cellRenderer(cellData, "deferral-type", fa.deferralType)
                  : "";
                // No deferralType in FA
              }}
            />
            <Column
              dataField="deferralPercentage"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`deferral-percentage`, "deferral", "percentage")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.deferralPercentage
                  ? cellRenderer(
                      cellData,
                      "deferral-percentage",
                      formatNumber(item?.deferralPercentage / 100, "#%")
                    )
                  : null;
              }}
            />

            <Column
              dataField="distributionElection"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`distribution-election`, "distribution", "election")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.distributionElection
                  ? cellRenderer(cellData, "distribution-election", item?.distributionElection)
                  : "";
              }}
            />
            <Column
              dataField="sharesDeferred"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`shares-deferred`, "Shares Deferred")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.sharesDeferred
                  ? cellRenderer(cellData, "shares-deferred", item?.sharesDeferred)
                  : null;
              }}
            />
          </DataGrid>
        </>
      ) : null}
    </div>
  );
}

export const DLTIPSkeleton = () => {
  return <GridSkeleton columns={5} rows={4} />;
};
